/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/16/22
 */

import React from "react";
import {
	FormControl,
	List,
	ListItem,
	ListItemButton,
	ListItemText, Pagination,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {useQuery} from "urql";
import {
	SavedCalendarsQuery, SavedCalendarsQueryVars,
	SavedCalendarsResponse,
} from "../graphql/queries/calendars";
import HeroMessage from "../components/HeroMessage";
import LoadingHero from "../components/LoadingHero";
import {Calendar} from "../interfaces/Global";
import {Link} from "react-router-dom";
import {format} from "../dates-helper";

const LIMIT_CALENDARS = 15;

export default function AllCalendars() {

	const [page, setPage] = React.useState(1);
	const [search, setSearch] = React.useState("");
	const [ calendarsResult ] = useQuery<SavedCalendarsResponse, SavedCalendarsQueryVars>({
		query: SavedCalendarsQuery,
		requestPolicy: "cache-and-network",
		variables: {
			search: search.length ? `%${search.toLowerCase()}%` : undefined,
			orderBy: "-id",
			offset: (page-1) * LIMIT_CALENDARS,
			limit: LIMIT_CALENDARS
		},
	});

	const calendars = calendarsResult.data?.savedCalendars.items || [];

	return (
		<Stack spacing={1}>
			<Typography variant={"h5"} sx={{ pb: 2 }}>All Calendars</Typography>
			<FormControl fullWidth>
				<TextField
					variant={"outlined"}
					label={"Search Calendars"}
					value={search}
					onChange={(event) => {
						setSearch(event.target.value);
					}}
				/>
			</FormControl>
			{calendarsResult.error &&
				<HeroMessage
					p={4}
					title={"Request Error"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={calendarsResult.error.message}
				/>
			}
			{calendarsResult.fetching &&
				<LoadingHero />
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length === 0 &&
				<Typography
					variant={"body1"}
					sx={{ pt: 2 }}
				>
					No calendars matching the search "{search}"
				</Typography>
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length > 0 &&
				<List dense disablePadding>
					{calendars.map(calendar =>
						<SavedCalendarItem
							key={calendar.id}
							calendar={calendar}
						/>)}
				</List>}
			{(calendarsResult.data?.savedCalendars.total || 0) > LIMIT_CALENDARS &&
			<Stack alignItems={"center"}>
				<Pagination
					page={page}
					onChange={(event, page) => {
						setPage(page);
					}}
					count={Math.ceil((calendarsResult.data?.savedCalendars.total || 0)/LIMIT_CALENDARS)}
				/>
			</Stack>}
		</Stack>
	);

}

function SavedCalendarItem ({ calendar } : { calendar: Calendar }) {

	return (
		<ListItem
			disableGutters
			disablePadding
			divider
		>
			<ListItemButton disableGutters component={Link} to={"/calendar/" + calendar.id}>
				<ListItemText
					primary={calendar.title}
					secondaryTypographyProps={{
						sx: {
							fontSize: "0.8em"
						}
					}}
					secondary={
						(calendar.createdByEmployee ?
								calendar.createdByEmployee?.displayName + " (" + calendar.createdBy + ")" :
								calendar.createdBy) + " • " + format(calendar.creationDate as Date, 'PPP p')}
				/>
			</ListItemButton>
		</ListItem>
	);
}